import React, { useMemo, useState } from 'react';

import { Clear, Search, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { Box, Chip, IconButton, InputAdornment, Skeleton, Stack, TextField, Tooltip, Typography, alpha } from '@mui/material';

import { usePersistedSetting } from '@@common';
import { GalleryHeader, GalleryStatistics } from '@@components';

import { IntegrationEventAndPayloadModel } from '../../../api/api';
import IntegrationEventSection from './IntegrationEventSection';

interface Props {
	integrationEvents: IntegrationEventAndPayloadModel[];
	loading?: boolean;
	selectedEventId?: string;
	title?: string;
	subtitle?: React.ReactNode;
	primaryAction?: React.ReactNode;
	utilityActions?: React.ReactNode;
}

const EXPANDED_EVENT_IDS_STORAGE_KEY = 'integrationEvents.expandedEventIds';

const normalizeSearch = (value: string | undefined) => value?.trim().toLocaleLowerCase() ?? '';

const includesSearch = (value: string | undefined, searchValue: string) => normalizeSearch(value).includes(searchValue);

const addExpandedEventIds = (current: string[], idsToAdd: string[]) => Array.from(new Set([...current, ...idsToAdd]));

const removeExpandedEventIds = (current: string[], idsToRemove: string[]) => {
	const idsToRemoveSet = new Set(idsToRemove);
	return current.filter((id) => !idsToRemoveSet.has(id));
};

const toggleExpandedEventId = (current: string[], eventId: string) => {
	if (current.includes(eventId)) {
		return current.filter((id) => id !== eventId);
	}

	return [...current, eventId];
};

const IntegrationEventList: React.FC<Props> = ({
	integrationEvents,
	loading = false,
	selectedEventId,
	title = 'Integration Events',
	subtitle = 'Browse published domain events and inspect their payload contracts.',
	primaryAction,
	utilityActions,
}) => {
	const [filter, setFilter] = useState('');
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [expandedEventIds, setExpandedEventIds] = usePersistedSetting<string[]>(EXPANDED_EVENT_IDS_STORAGE_KEY, []);

	const searchValue = normalizeSearch(filter);
	const availableTags = useMemo(
		() => Array.from(new Set(integrationEvents.flatMap((item) => item.event.tags ?? []))).sort((a, b) => a.localeCompare(b)),
		[integrationEvents],
	);
	const selectedTagSet = useMemo(() => new Set(selectedTags), [selectedTags]);
	const filteredEvents = useMemo(() => {
		return integrationEvents.filter((item) => {
			const event = item.event;
			const tagMatches = selectedTags.length === 0 || (event.tags ?? []).some((tag) => selectedTagSet.has(tag));
			const searchMatches =
				!searchValue ||
				includesSearch(event.name, searchValue) ||
				includesSearch(event.description, searchValue) ||
				(event.tags ?? []).some((tag) => includesSearch(tag, searchValue));

			return tagMatches && searchMatches;
		});
	}, [integrationEvents, searchValue, selectedTagSet, selectedTags.length]);
	const expandedEventIdSet = useMemo(() => new Set(expandedEventIds), [expandedEventIds]);

	React.useEffect(() => {
		if (!searchValue && selectedTags.length === 0) {
			return;
		}

		setExpandedEventIds((current) =>
			addExpandedEventIds(
				current,
				filteredEvents.map((item) => item.event.id),
			),
		);
	}, [filteredEvents, searchValue, selectedTags.length, setExpandedEventIds]);

	React.useEffect(() => {
		if (selectedEventId) {
			setExpandedEventIds((current) => addExpandedEventIds(current, [selectedEventId]));
		}
	}, [selectedEventId, setExpandedEventIds]);

	const handleToggleEvent = (eventId: string) => {
		setExpandedEventIds((current) => toggleExpandedEventId(current, eventId));
	};

	const handleExpandAll = () => {
		setExpandedEventIds((current) =>
			addExpandedEventIds(
				current,
				filteredEvents.map((item) => item.event.id),
			),
		);
	};

	const handleCollapseAll = () => {
		setExpandedEventIds((current) =>
			removeExpandedEventIds(
				current,
				filteredEvents.map((item) => item.event.id),
			),
		);
	};

	const handleToggleTag = (tag: string) => {
		setSelectedTags((current) => (current.includes(tag) ? current.filter((selectedTag) => selectedTag !== tag) : [...current, tag]));
	};

	return (
		<Stack direction="column" spacing={1.5} sx={{ minHeight: 0, flexGrow: 1 }}>
			<GalleryHeader
				title={title}
				subtitle={subtitle}
				utilityActions={utilityActions}
				primaryAction={primaryAction}
				statistics={
					<GalleryStatistics
						items={[
							{ label: 'Events', value: integrationEvents.length },
							{ label: 'Tags', value: availableTags.length },
						]}
					/>
				}
				search={
					<TextField
						value={filter}
						onChange={(event) => setFilter(event.target.value)}
						placeholder="Search events, descriptions, or tags"
						size="small"
						fullWidth
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							),
							endAdornment: filter ? (
								<InputAdornment position="end">
									<IconButton aria-label="Clear search" size="small" onClick={() => setFilter('')}>
										<Clear fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : undefined,
						}}
					/>
				}
				searchActions={
					<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="flex-end">
						{availableTags.map((tag) => {
							const selected = selectedTagSet.has(tag);
							return (
								<Chip
									key={tag}
									label={tag}
									size="small"
									variant={selected ? 'filled' : 'outlined'}
									color={selected ? 'success' : 'default'}
									onClick={() => handleToggleTag(tag)}
								/>
							);
						})}
						{selectedTags.length > 0 && <Chip label="Clear tags" size="small" onClick={() => setSelectedTags([])} />}
						<Tooltip title="Expand all">
							<span>
								<IconButton aria-label="Expand all events" size="small" onClick={handleExpandAll} disabled={filteredEvents.length === 0}>
									<UnfoldMore fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Collapse all">
							<span>
								<IconButton aria-label="Collapse all events" size="small" onClick={handleCollapseAll} disabled={filteredEvents.length === 0}>
									<UnfoldLess fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Stack>
				}
			/>
			<Box sx={{ p: 2, minHeight: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<Stack spacing={1.5} sx={{ overflow: 'auto', pr: 0.5, minHeight: 0 }}>
					{loading && integrationEvents.length === 0 ? (
						<>
							<Skeleton variant="rounded" height={86} />
							<Skeleton variant="rounded" height={86} />
						</>
					) : filteredEvents.length === 0 && integrationEvents.length > 0 ? (
						<Box
							sx={{
								border: (theme) => `1px dashed ${theme.palette.divider}`,
								borderRadius: 1,
								py: 6,
								textAlign: 'center',
							}}
						>
							<Typography variant="subtitle1">No events found</Typography>
							<Typography variant="body2" color="text.secondary">
								Adjust the search text or tag filters.
							</Typography>
						</Box>
					) : filteredEvents.length === 0 ? (
						<Box
							sx={{
								border: (theme) => `1px dashed ${theme.palette.divider}`,
								borderRadius: 1,
								py: 6,
								textAlign: 'center',
							}}
						>
							<Typography variant="subtitle1">No events yet</Typography>
							<Typography variant="body2" color="text.secondary">
								Create an event from a context to publish it in this catalog.
							</Typography>
						</Box>
					) : (
						filteredEvents.map((event) => (
							<IntegrationEventSection
								key={event.event.id}
								eventData={event}
								expanded={expandedEventIdSet.has(event.event.id)}
								onTagClick={handleToggleTag}
								onToggle={handleToggleEvent}
							/>
						))
					)}
				</Stack>
			</Box>
		</Stack>
	);
};

export default IntegrationEventList;
